import {
  createDraftSafeSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'

import { callApi } from 'store/api/yay/call'

import { CallUser } from './agora'

type CallChatMessage = {
  id: string
  text: string
  userId?: string
  nickname?: string
  deleted?: boolean
  createdAt?: number
}

export type CallChatState = {
  users: Record<string, Partial<CallUser>>
  messages: Array<CallChatMessage>
  pinnedMessage: CallChatMessage
  showLastMessage: boolean
  haveNewMessage: boolean
  lastViewedMessageId: string
  unreadFromMessageId: string
}

export const callChatInitialState: CallChatState = {
  users: {},
  messages: [],
  pinnedMessage: null,
  showLastMessage: false,
  haveNewMessage: false,
  lastViewedMessageId: null,
  unreadFromMessageId: null,
}

export const callChatSlice = createSlice({
  name: 'callChat',
  initialState: callChatInitialState,
  reducers: {
    addCallChatMessage(state, action: PayloadAction<CallChatMessage>) {
      state.messages.push(action.payload)
    },
    deleteCallChatMessage(state, action: PayloadAction<string>) {
      const index = state.messages.findIndex((m) => m.id === action.payload)
      if (index !== -1) state.messages[index].deleted = true
    },
    receiveCallChatMessage(
      state,
      action: PayloadAction<{ message: CallChatMessage; user: CallUser }>
    ) {
      const { message, user: sender } = action.payload
      if (!state.users[message.userId]) {
        state.users[message.userId] = sender
      }

      state.messages.push(message)
      state.showLastMessage = true
    },
    pinCallChatMessage(state, action: PayloadAction<CallChatMessage>) {
      state.pinnedMessage = action.payload
    },
    unpinCallChatMessage(state) {
      state.pinnedMessage = null
    },
    setCallChatSetting<
      T extends keyof Omit<
        CallChatState,
        'users' | 'messages' | 'pinnedMessage'
      >
    >(
      state: CallChatState,
      action: PayloadAction<{ key: T; value: CallChatState[T] }>
    ) {
      state[action.payload.key] = action.payload.value
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      callApi.endpoints.getCallDetails.matchFulfilled,
      (state, action) => {
        Object.entries(action.payload.users || {}).forEach(([userId, user]) => {
          state.users[userId] = {
            id: user.id,
            nickname: user.nickname,
            role: user.role,
            profile_icon_thumbnail: user.profile_icon_thumbnail,
          }
        })
      }
    )
  },
})

export const selectCallChat = (state): CallChatState => state.callChat
export const selectCallChatUsers = createDraftSafeSelector(
  selectCallChat,
  (state) => state.users
)
export const selectCallChatUser = createDraftSafeSelector(
  [selectCallChatUsers, (state, id: string) => id],
  (state, id) => state.users[id]
)

export const {
  setCallChatSetting,
  addCallChatMessage,
  deleteCallChatMessage,
  pinCallChatMessage,
  unpinCallChatMessage,
  receiveCallChatMessage,
} = callChatSlice.actions

export default callChatSlice.reducer
